/**
 * SceneFlow — the calm scene-transition helper. Every navigation is a gentle
 * cross-fade (fade out -> start next -> fade in) so the world never snaps or
 * flashes (CLAUDE.md pillar 1: when in doubt, choose calm). Respects reduced
 * motion: when set, transitions are instant (no fade) but still clean.
 *
 * Scenes call `SceneFlow.goTo(this, 'Town')`. The helper handles the camera fades
 * and the start/stop handoff; it owns no game rules.
 */
import Phaser from 'phaser';
import { TIMING } from './ui/theme.js';

/** Module-level flag scenes can flip from Settings.reducedMotion. */
let reducedMotion = false;

export function setReducedMotion(value: boolean): void {
  reducedMotion = value;
}

export function isReducedMotion(): boolean {
  return reducedMotion;
}

export interface GoToOptions {
  /** Data passed to the target scene's init/create. */
  readonly data?: Record<string, unknown>;
  /** Override the fade duration (ms). */
  readonly duration?: number;
  /** Keep the current scene running underneath (for overlay scenes like Chat). */
  readonly overlay?: boolean;
}

/**
 * Fade `scene` out, then start `targetKey` (which fades itself in via `fadeIn`).
 * When reduced-motion is on, switches instantly.
 *
 * RELIABILITY (the "stuck in Town" bug): the launch MUST always run. On a re-entered
 * scene the camera's fade effect can carry STALE state (a prior fade already marked
 * complete), so `FADE_OUT_COMPLETE` may never fire again and `scene.start` would be
 * silently skipped — stranding the player. To guarantee the handoff we:
 *   1) RESET the camera fade FX first so `fadeOut` runs from a clean state every time;
 *   2) guard `launch` with a once-flag so it can never run twice;
 *   3) arm a `delayedCall` fallback (fade duration + margin) that runs `launch` even if
 *      the FADE_OUT_COMPLETE event never arrives;
 *   4) if the camera is missing/unable to fade, call `launch` directly.
 * Net effect: every navigation starts its target scene, fade or no fade.
 */
export function goTo(scene: Phaser.Scene, targetKey: string, options: GoToOptions = {}): void {
  const duration = options.duration ?? (reducedMotion ? 0 : TIMING.fade);

  let launched = false;
  const launch = (): void => {
    if (launched) return;
    launched = true;
    if (options.overlay) {
      scene.scene.launch(targetKey, options.data);
    } else {
      scene.scene.start(targetKey, options.data);
    }
  };

  if (duration <= 0) {
    launch();
    return;
  }

  const cam = scene.cameras.main;
  if (!cam) {
    // No camera to fade through — just hand off immediately rather than strand.
    launch();
    return;
  }

  // Clear any stale fade/flash FX so fadeOut starts fresh on a RE-ENTERED scene; without
  // this the effect can sit already-"complete" and never re-fire FADE_OUT_COMPLETE.
  cam.resetFX();

  cam.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, launch);

  // Robust fallback: if the event never fires (stale FX, an interrupted fade), launch
  // anyway shortly after the fade should have finished. The once-flag keeps it single.
  scene.time.delayedCall(duration + 120, launch);

  cam.fadeOut(duration, 0, 0, 0);
}

/**
 * Fade a scene IN from black — call at the end of a scene's `create()`. No-op (but
 * ensures the camera is visible) under reduced motion.
 */
export function fadeIn(scene: Phaser.Scene, duration?: number): void {
  const d = duration ?? (reducedMotion ? 0 : TIMING.fade);
  if (d <= 0) {
    scene.cameras.main.resetFX();
    return;
  }
  scene.cameras.main.fadeIn(d, 0, 0, 0);
}

/** Stop an overlay scene started with `{ overlay: true }`, fading it out first. */
export function closeOverlay(scene: Phaser.Scene, duration?: number): void {
  const d = duration ?? (reducedMotion ? 0 : TIMING.fade);
  if (d <= 0) {
    scene.scene.stop();
    return;
  }
  scene.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () =>
    scene.scene.stop(),
  );
  scene.cameras.main.fadeOut(d, 0, 0, 0);
}

/** Convenience namespace import surface. */
export const SceneFlow = {
  goTo,
  fadeIn,
  closeOverlay,
  setReducedMotion,
  isReducedMotion,
};
